import { Search, Calendar, ChevronDown, LayoutGrid, ChevronLeft, ChevronRight, CalendarRange } from "lucide-react";
import { Button } from "../ui/button";
import { TeamMemberFilter } from "./TeamMemberFilter";
import { StatusFilter } from "./StatusFilter";
import { motion } from "motion/react";
import { ViewMode } from "../KanbanBoardPage";
import { StatusInterface } from "../../../../models/TicketModule/Status";
import React, { useLayoutEffect, useRef, useState } from "react";
import { FormViewType } from "../../Tickets/Tickets";
import { UserInterface } from "../../../../models/UserModel/User";
import { useLanguageStore } from "../../../../store/useLanguageStore";
import { StorageItems } from "../../../../utils/storageItems";

interface KanbanHeaderProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  teamMembers: UserInterface[];
  selectedMembers: string[];
  onSelectedMembersChange: (members: string[] | ((prev: string[]) => string[])) => void;
  statuses: StatusInterface[];
  selectedStatuses: string[];
  onSelectedStatusesChange: (statuses: string[]) => void;
  currentWeekStart: Date;
  onWeekChange: (date: Date) => void;
  selectedDay?: Date;
  onDayChange?: (date: Date) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  formView: FormViewType;
  setFormView: React.Dispatch<React.SetStateAction<FormViewType>>;
  darkMode: boolean;
  customStartDate: string;
  customEndDate: string;
  onCustomStartDateChange: (value: string) => void;
  onCustomEndDateChange: (value: string) => void;
}

function KanbanHeaderComponent({
  viewMode,
  onViewModeChange,
  teamMembers,
  selectedMembers,
  onSelectedMembersChange,
  statuses,
  selectedStatuses,
  onSelectedStatusesChange,
  currentWeekStart,
  onWeekChange,
  selectedDay,
  onDayChange,
  searchQuery,
  onSearchChange,
  formView,
  setFormView,
  darkMode,
  customStartDate,
  customEndDate,
  onCustomStartDateChange,
  onCustomEndDateChange,
}: KanbanHeaderProps) {
  const { language } = useLanguageStore();
  const headerRowRef = useRef<HTMLDivElement | null>(null);
  const rightGroupRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);

  const [hideTitle, setHideTitle] = useState(false);
  const titleWidthRef = useRef<number>(0);

  const measureHostRef = useRef<HTMLDivElement | null>(null);
  const measureWeekRef = useRef<HTMLDivElement | null>(null);
  const [reservedMinHeight, setReservedMinHeight] = useState<number>(62);

  useLayoutEffect(() => {
    const row = headerRowRef.current;
    if (!row) return;

    if (titleRef.current) {
      titleWidthRef.current = titleRef.current.getBoundingClientRect().width;
    }

    const ro = new ResizeObserver(() => {
      const rowW = row.getBoundingClientRect().width;
      const rightW = rightGroupRef.current?.getBoundingClientRect().width ?? 0;

      const buffer = 24;
      const shouldHide = rowW < rightW + titleWidthRef.current + buffer;
      setHideTitle(shouldHide);
    });

    ro.observe(row);
    return () => ro.disconnect();
  }, []);

  const getWeekDates = (startDate: Date) => {
    const dates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const weekDates = getWeekDates(currentWeekStart);

  const formatWeekRange = () => {
    const start = currentWeekStart.toLocaleDateString("sq-AL", { day: "numeric", month: "short" });
    const end = weekDates[6].toLocaleDateString("sq-AL", { day: "numeric", month: "short", year: "numeric" });
    return `${start} - ${end}`;
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() - 7);
    onWeekChange(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + 7);
    onWeekChange(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    onWeekChange(monday);
  };

  useLayoutEffect(() => {
    const host = measureHostRef.current;
    if (!host) return;

    const update = () => {
      const weekH = measureWeekRef.current?.getBoundingClientRect().height ?? 0;
      const next = weekH > 62 ? 128 : 62;
      setReservedMinHeight(next);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(host);

    return () => ro.disconnect();
  }, [currentWeekStart, selectedDay, viewMode]);

  return (
    <div className="bg-white border-b border-gray-200">
      <div className="px-4 sm:px-6 py-4">
        <div ref={headerRowRef} className="flex items-center justify-between gap-3 mb-4 flex-wrap min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 min-w-0">
            <h1 ref={titleRef} className={`${hideTitle ? "hidden" : "block"} text-gray-900 whitespace-nowrap`}>
              Kanban Board
            </h1>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant={viewMode === "board" ? "selected" : "outline"}
                size="sm"
                onClick={() => onViewModeChange("board")}
              >
                <CalendarRange className="w-4 h-4 mr-2" />
                {language["Board"] ?? "Board"}
              </Button>

              <Button
                variant={viewMode === "week" ? "selected" : "outline"}
                size="sm"
                onClick={() => onViewModeChange("week")}
              >
                <Calendar className="w-4 h-4 mr-2" />
                {language["week"] ?? "Week"}
              </Button>

              <Button
                variant={viewMode === "day" ? "selected" : "outline"}
                size="sm"
                onClick={() => onViewModeChange("day")}
              >
                {language["day"] ?? "Day"}
              </Button>
            </div>
          </div>

          <div
            ref={rightGroupRef}
            className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full lg:w-auto"
          >
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={language["Search"] ?? "Search"}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <TeamMemberFilter
                teamMembers={teamMembers}
                selectedMembers={selectedMembers}
                onSelectedMembersChange={onSelectedMembersChange}
                darkMode={darkMode}
              />

              <StatusFilter
                statuses={statuses}
                selectedStatuses={selectedStatuses}
                onSelectedStatusesChange={onSelectedStatusesChange}
                darkMode={darkMode}
              />

              <div className="relative">
                <LayoutGrid className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <select
                  value={formView}
                  onChange={(e) => {
                    setFormView(e.target.value as FormViewType);
                    localStorage.setItem(StorageItems.TicketView, e.target.value as FormViewType);
                  }}
                  className="h-8 pl-9 pr-8 rounded-md border border-gray-300 bg-white text-sm text-gray-700
                         appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="TableView">{language["TableView"] ?? "TableView"}</option>
                  <option value="BoardView">{language["BoardView"] ?? "BoardView"}</option>
                </select>
                <ChevronDown className="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        <div
          ref={measureHostRef}
          className="min-w-0 flex items-center transition-[min-height] duration-200"
          style={{ minHeight: reservedMinHeight }}
        >
          <div className="absolute opacity-0 pointer-events-none -z-10">
            <div ref={measureWeekRef} className="flex flex-wrap items-center gap-4 min-w-0">
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 border rounded-md" />
                  <div className="h-8 w-16 border rounded-md" />
                  <div className="h-8 w-8 border rounded-md" />
                </div>
                <div className="text-sm whitespace-nowrap px-[10px]">{formatWeekRange()}</div>
              </div>

              <div className="flex-1 min-w-0 basis-full lg:basis-auto flex gap-2 overflow-x-auto">
                {weekDates.map((d, i) => (
                  <div key={i} className="flex-1 min-w-32 px-3 py-2 rounded-lg border">
                    <div className="text-xs mb-1">{d.toLocaleDateString("sq-AL", { weekday: "short" })}</div>
                    <div>{d.toLocaleDateString("sq-AL", { day: "numeric", month: "short" })}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {(viewMode === "week" || viewMode === "day" || viewMode === "board") && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="flex flex-wrap items-center gap-4 min-w-0 w-full"
            >
              {viewMode === "board" && (
                <div className="flex flex-wrap items-center gap-3 w-full">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 whitespace-nowrap">{language["StartTime"] ?? "Start"}</span>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => onCustomStartDateChange(e.target.value)}
                      className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-700
                               focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 whitespace-nowrap">{language["EndTime"] ?? "End"}</span>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => onCustomEndDateChange(e.target.value)}
                      className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-700
                               focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              {(viewMode === "week" || viewMode === "day") && (
                <>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={goToPreviousWeek}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>

                      <Button variant="outline" size="sm" onClick={goToToday}>
                        {language["Today"] ?? "Sot"}
                      </Button>

                      <Button variant="outline" size="sm" onClick={goToNextWeek}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="text-sm text-gray-600 whitespace-nowrap px-[10px]">{formatWeekRange()}</div>
                  </div>

                  {viewMode === "week" && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.1 }}
                      className="flex-1 min-w-0 basis-full lg:basis-auto flex gap-2 overflow-x-auto"
                    >
                      {weekDates.map((date, index) => {
                        const isToday = date.toDateString() === new Date().toDateString();
                        return (
                          <motion.button
                            key={index}
                            onClick={() => {
                              if (onDayChange) {
                                onDayChange(date);
                                onViewModeChange("day");
                              }
                            }}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.05 * index, type: "spring", stiffness: 300, damping: 25 }}
                            className={`flex-1 min-w-32 px-3 py-2 rounded-lg border text-center transition-all duration-200 cursor-pointer ${
                              isToday
                                ? "bg-blue-50 border-blue-200 text-blue-700 shadow-sm"
                                : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-white hover:border-gray-300 hover:shadow-sm"
                            }`}
                          >
                            <div className="text-xs text-gray-500 mb-1">
                              {date.toLocaleDateString("sq-AL", { weekday: "short" })}
                            </div>
                            <div>{date.toLocaleDateString("sq-AL", { day: "numeric", month: "short" })}</div>
                          </motion.button>
                        );
                      })}
                    </motion.div>
                  )}

                  {viewMode === "day" && selectedDay && onDayChange && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.1 }}
                      className="flex-1 min-w-0 basis-full lg:basis-auto flex gap-2 overflow-x-auto"
                    >
                      {weekDates.map((date, index) => {
                        const isToday = date.toDateString() === new Date().toDateString();
                        const isSelected = selectedDay.toDateString() === date.toDateString();
                        return (
                          <motion.button
                            key={index}
                            onClick={() => onDayChange(date)}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.05 * index, type: "spring", stiffness: 300, damping: 25 }}
                            className={`flex-1 min-w-32 px-3 py-2 rounded-lg border text-center transition-all duration-200 cursor-pointer ${
                              isSelected
                                ? "bg-blue-500 border-blue-500 text-white shadow-lg"
                                : isToday
                                ? "bg-blue-50 border-blue-200 text-blue-700 shadow-sm hover:bg-blue-100 hover:shadow-md"
                                : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-white hover:border-gray-300 hover:shadow-sm"
                            }`}
                          >
                            <div
                              className={`text-xs mb-1 transition-colors duration-200 ${
                                isSelected ? "text-blue-100" : "text-gray-500"
                              }`}
                            >
                              {date.toLocaleDateString("sq-AL", { weekday: "short" })}
                            </div>
                            <div className="transition-colors duration-200">
                              {date.toLocaleDateString("sq-AL", { day: "numeric", month: "short" })}
                            </div>
                          </motion.button>
                        );
                      })}
                    </motion.div>
                  )}
                </>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

export const KanbanHeader = React.memo(KanbanHeaderComponent);
